import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2, Wrench, X } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Field, Input, Select, Textarea, Toggle } from '@/components/ui/Input';
import { ConfirmDialog, Modal } from '@/components/ui/Modal';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/ui/States';
import { Table, TableWrapper, Td, Th, Tr } from '@/components/ui/Table';
import { useToast } from '@/components/ui/Toast';
import { cn } from '@/lib/cn';

const KATEGORI = {
  scraping: 'Scraping',
  eda: 'EDA',
  analytics: 'Analytics',
  ml: 'Machine Learning',
  tools: 'Tools',
};

const TIPE_PARAM = {
  text: 'Teks',
  number: 'Angka',
  boolean: 'Ya/Tidak',
  pilihan: 'Pilihan (dropdown)',
};

const PARAM_KOSONG = {
  name: '',
  label: '',
  type: 'text',
  options: [],
  defaultValue: '',
  required: true,
  help: '',
};

const INPUT_TYPE = {
  none: 'Tidak ada (skrip menentukan sendiri)',
  file: 'Berkas dari Penyimpanan Pribadi',
  job: 'Hasil pekerjaan scraping',
};

const FORM_KOSONG = {
  name: '',
  category: 'scraping',
  inputType: 'none',
  website: '',
  section: '',
  description: '',
  targetInfo: '',
  scriptPath: '',
  params: [],
  outputFormats: ['csv', 'json'],
  maxRows: 1000,
  paket: [],
  penggunaKhusus: [],
  active: true,
};

/** Baris penyunting satu parameter di dalam formulir template. */
const BarisParam = ({ param, index, onUbah, onHapus }) => (
  <div className="space-y-3 rounded-lg border border-[var(--border-subtle)] p-3">
    <div className="flex items-start justify-between gap-2">
      <p className="text-xs font-bold tracking-wide text-[var(--text-muted)] uppercase">
        Parameter {index + 1}
      </p>
      <Button
        variant="ghost"
        size="sm"
        icon={Trash2}
        iconOnly
        aria-label={`Hapus parameter ${index + 1}`}
        onClick={onHapus}
      />
    </div>

    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="Nama teknis" hint="huruf kecil, mis. kata_kunci">
        {(props) => (
          <Input
            {...props}
            value={param.name}
            placeholder="kata_kunci"
            onChange={(e) => onUbah({ ...param, name: e.target.value })}
          />
        )}
      </Field>
      <Field label="Label tampilan">
        {(props) => (
          <Input
            {...props}
            value={param.label}
            placeholder="Kata kunci pencarian"
            onChange={(e) => onUbah({ ...param, label: e.target.value })}
          />
        )}
      </Field>
      <Field label="Tipe isian">
        {(props) => (
          <Select
            {...props}
            value={param.type}
            onChange={(e) => onUbah({ ...param, type: e.target.value })}
          >
            {Object.entries(TIPE_PARAM).map(([nilai, label]) => (
              <option key={nilai} value={nilai}>
                {label}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Nilai bawaan">
        {(props) => (
          <Input
            {...props}
            value={param.defaultValue}
            onChange={(e) => onUbah({ ...param, defaultValue: e.target.value })}
          />
        )}
      </Field>
    </div>

    {param.type === 'pilihan' && (
      <Field label="Daftar pilihan" hint="Dipisah koma. Inilah isi dropdown yang dilihat user.">
        {(props) => (
          <Input
            {...props}
            value={param.options.join(', ')}
            placeholder="mystery, travel, fiction"
            onChange={(e) =>
              onUbah({
                ...param,
                options: e.target.value.split(',').map((v) => v.trim()).filter(Boolean),
              })
            }
          />
        )}
      </Field>
    )}

    <div className="flex flex-wrap items-center gap-4">
      <Toggle
        checked={param.required}
        onChange={(v) => onUbah({ ...param, required: v })}
        label="Wajib diisi"
      />
      <div className="min-w-48 flex-1">
        <Input
          value={param.help}
          placeholder="Keterangan singkat untuk user (opsional)"
          aria-label="Keterangan parameter"
          className="h-9 px-3 text-sm"
          onChange={(e) => onUbah({ ...param, help: e.target.value })}
        />
      </div>
    </div>
  </div>
);

const FormTemplate = ({ awal, onClose, onSukses }) => {
  // Daftar paket diambil dari sumber yang sama dengan halaman Harga, jadi
  // paket yang baru ditambahkan admin langsung muncul di sini tanpa perlu
  // ada yang mengingat untuk memperbaruinya di dua tempat.
  const { data: dataPaket } = useQuery({
    queryKey: ['public-plans'],
    queryFn: () => api.get('/public/plans'),
  });
  const daftarPaket = (dataPaket?.data ?? []).map((p) => ({ key: p.key ?? p.id, name: p.name }));

  const toast = useToast();
  const [form, setForm] = useState(awal ?? FORM_KOSONG);
  const sedangSunting = Boolean(awal?.id);

  const simpan = useMutation({
    mutationFn: () => {
      const body = { ...form, maxRows: Number(form.maxRows) || 1000 };
      delete body.id;
      delete body.createdAt;
      delete body.updatedAt;
      delete body.createdBy;
      delete body.deletedAt;
      return sedangSunting
        ? api.patch(`/platform/templates/${awal.id}`, body)
        : api.post('/platform/templates', body);
    },
    onSuccess: () => {
      toast.success(sedangSunting ? 'Template diperbarui' : 'Template dibuat', {
        description: 'User langsung melihatnya di halaman scraping.',
      });
      onSukses();
    },
    onError: (err) =>
      toast.error('Gagal menyimpan', {
        description: err instanceof ApiError ? err.message : 'Periksa isian lalu coba lagi.',
      }),
  });

  const set = (kunci, nilai) => setForm((prev) => ({ ...prev, [kunci]: nilai }));

  return (
    <Modal
      open
      onClose={onClose}
      title={sedangSunting ? 'Sunting Template' : 'Template Baru'}
      description="Skrip Python yang sudah ada di server, dibungkus jadi formulir untuk user."
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button loading={simpan.isPending} onClick={() => simpan.mutate()}>
            {sedangSunting ? 'Simpan Perubahan' : 'Buat Template'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nama" required>
            {(props) => (
              <Input
                {...props}
                value={form.name}
                placeholder="Pencarian Produk"
                onChange={(e) => set('name', e.target.value)}
              />
            )}
          </Field>
          <Field label="Kategori">
            {(props) => (
              <Select {...props} value={form.category} onChange={(e) => set('category', e.target.value)}>
                {Object.entries(KATEGORI).map(([nilai, label]) => (
                  <option key={nilai} value={nilai}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label="Sumber Data"
            hint="Untuk EDA dan ML, pilih berkas: user akan diminta memilih datanya."
          >
            {(props) => (
              <Select
                {...props}
                value={form.inputType}
                onChange={(e) => set('inputType', e.target.value)}
              >
                {Object.entries(INPUT_TYPE).map(([nilai, label]) => (
                  <option key={nilai} value={nilai}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Website" required hint="Dropdown pertama yang dilihat user, mis. tokopedia.">
            {(props) => (
              <Input
                {...props}
                value={form.website}
                placeholder="tokopedia"
                onChange={(e) => set('website', e.target.value.toLowerCase())}
              />
            )}
          </Field>
          <Field label="Bagian" required hint="Dropdown kedua, mis. search.">
            {(props) => (
              <Input
                {...props}
                value={form.section}
                placeholder="search"
                onChange={(e) => set('section', e.target.value.toLowerCase())}
              />
            )}
          </Field>
        </div>

        <Field label="Path skrip" required hint="Relatif terhadap folder lab/ di server. Berkasnya harus sudah ada.">
          {(props) => (
            <Input
              {...props}
              value={form.scriptPath}
              placeholder="scripts/tokopedia/search.py"
              className="font-mono"
              onChange={(e) => set('scriptPath', e.target.value)}
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Keterangan halaman sasaran" hint='Tampil ke user, mis. "tokopedia.com/search?".'>
            {(props) => (
              <Input
                {...props}
                value={form.targetInfo}
                onChange={(e) => set('targetInfo', e.target.value)}
              />
            )}
          </Field>
          <Field label="Batas baris" hint="User boleh meminta lebih sedikit, tidak lebih banyak.">
            {(props) => (
              <Input
                {...props}
                type="number"
                min="1"
                max="100000"
                value={form.maxRows}
                onChange={(e) => set('maxRows', e.target.value)}
              />
            )}
          </Field>
        </div>

        <Field label="Deskripsi">
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
            />
          )}
        </Field>

        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-[var(--text-primary)]">
            Parameter ({form.params.length})
          </p>
          <Button
            variant="secondary"
            size="sm"
            icon={Plus}
            onClick={() => set('params', [...form.params, { ...PARAM_KOSONG }])}
          >
            Tambah Parameter
          </Button>
        </div>

        {form.params.map((param, i) => (
          <BarisParam
            key={i}
            param={param}
            index={i}
            onUbah={(baru) => set('params', form.params.map((p, idx) => (idx === i ? baru : p)))}
            onHapus={() => set('params', form.params.filter((_, idx) => idx !== i))}
          />
        ))}

        <GerbangAkses
          paket={form.paket}
          penggunaKhusus={form.penggunaKhusus}
          daftarPaket={daftarPaket}
          onUbahPaket={(v) => set('paket', v)}
          onUbahPengguna={(v) => set('penggunaKhusus', v)}
        />

        <div className="flex flex-wrap items-center gap-6">
          <Toggle
            checked={form.outputFormats.includes('csv')}
            onChange={(v) =>
              set(
                'outputFormats',
                v
                  ? [...new Set([...form.outputFormats, 'csv'])]
                  : form.outputFormats.filter((f) => f !== 'csv'),
              )
            }
            label="Keluaran CSV"
          />
          <Toggle
            checked={form.outputFormats.includes('json')}
            onChange={(v) =>
              set(
                'outputFormats',
                v
                  ? [...new Set([...form.outputFormats, 'json'])]
                  : form.outputFormats.filter((f) => f !== 'json'),
              )
            }
            label="Keluaran JSON"
          />
          <Toggle checked={form.active} onChange={(v) => set('active', v)} label="Aktif" />
        </div>
      </div>
    </Modal>
  );
};

/**
 * Gerbang akses: paket mana yang boleh, plus pengguna yang dikecualikan.
 *
 * ── Kenapa tidak ada pilihan "semua paket" yang eksplisit ────────────────
 *
 * Tidak ada satu pun paket tercentang BERARTI terbuka untuk semua. Menambah
 * kotak "semua" di samping daftar paket menciptakan dua cara menyatakan hal
 * yang sama, dan begitu keduanya bisa aktif bersamaan, yang membacanya harus
 * menebak mana yang menang.
 *
 * Keadaan itu ditulis apa adanya di layar supaya tidak perlu ditebak.
 */
const GerbangAkses = ({ paket, penggunaKhusus, daftarPaket, onUbahPaket, onUbahPengguna }) => {
  const [cari, setCari] = useState('');
  const terbuka = !paket?.length;

  // Pencarian hanya berjalan setelah dua huruf. Meminta seluruh daftar
  // pengguna pada huruf pertama mengirim ribuan baris untuk sesuatu yang
  // hampir pasti langsung diketik ulang.
  const { data: hasilCari } = useQuery({
    queryKey: ['cari-pengguna', cari],
    queryFn: () => api.get('/platform/pencarian-pengguna', { params: { search: cari, limit: 8 } }),
    enabled: cari.trim().length >= 2,
  });

  // Nama pengguna yang sudah dipilih perlu ditampilkan walau tidak sedang
  // dicari, kalau tidak yang terlihat cuma deretan id yang tak berarti.
  const { data: dataTerpilih } = useQuery({
    queryKey: ['pengguna-terpilih', (penggunaKhusus ?? []).join(',')],
    queryFn: () => api.get('/platform/pencarian-pengguna', {
        params: { ids: penggunaKhusus.join(','), limit: 100 },
      }),
    enabled: (penggunaKhusus ?? []).length > 0,
  });

  const terpilih = dataTerpilih?.data ?? [];
  const kandidat = (hasilCari?.data ?? []).filter((u) => !(penggunaKhusus ?? []).includes(u.id));

  return (
    <div className="space-y-4 rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-sunken)] p-4">
      <div>
        <p className="text-sm font-bold text-[var(--text-primary)]">Siapa yang boleh memakai</p>
        <p className="mt-0.5 text-xs text-[var(--text-muted)]">
          {terbuka
            ? 'Tidak ada paket dipilih, jadi skrip ini terbuka untuk semua paket.'
            : `Hanya ${paket.length} paket di bawah ini, ditambah pengguna yang Anda sebut khusus.`}
        </p>
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold text-[var(--text-secondary)]">Paket</p>
        <div className="flex flex-wrap gap-1.5">
          {daftarPaket.map((p) => {
            const aktif = paket?.includes(p.key);
            return (
              <button
                key={p.key}
                type="button"
                onClick={() =>
                  onUbahPaket(
                    aktif ? paket.filter((x) => x !== p.key) : [...(paket ?? []), p.key],
                  )
                }
                className={cn(
                  'rounded-full border px-3 py-1 text-xs font-bold transition-colors',
                  aktif
                    ? 'border-accent-300 bg-accent-50 text-accent-700 dark:border-accent-500/40 dark:bg-accent-500/15 dark:text-accent-300'
                    : 'border-[var(--border-default)] bg-[var(--surface-raised)] text-[var(--text-muted)] hover:text-[var(--text-primary)]',
                )}
              >
                {p.name}
              </button>
            );
          })}
        </div>
        {!terbuka && (
          <button
            type="button"
            onClick={() => onUbahPaket([])}
            className="mt-2 text-xs font-semibold text-accent-600 hover:underline dark:text-accent-400"
          >
            Kosongkan, buka untuk semua paket
          </button>
        )}
      </div>

      <div>
        <p className="mb-1 text-xs font-semibold text-[var(--text-secondary)]">
          Pengguna yang tetap boleh, apa pun paketnya
        </p>
        <p className="mb-2 text-xs text-[var(--text-muted)]">
          Untuk memberi contoh ke calon pelanggan, atau membuka fitur bagi pemakai lama yang
          paketnya sudah tidak dijual. Selalu menambah, tidak pernah melarang.
        </p>

        {terpilih.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {terpilih.map((u) => (
              <span
                key={u.id}
                className="inline-flex items-center gap-1.5 rounded-full bg-[var(--surface-raised)] py-1 pr-1.5 pl-2.5 text-xs font-semibold text-[var(--text-primary)] ring-1 ring-[var(--border-default)]"
              >
                {u.email}
                <button
                  type="button"
                  aria-label={`Hapus ${u.email}`}
                  onClick={() => onUbahPengguna(penggunaKhusus.filter((x) => x !== u.id))}
                  className="rounded-full p-0.5 text-[var(--text-muted)] hover:bg-danger-50 hover:text-danger-600 dark:hover:bg-danger-500/15"
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        <Input
          value={cari}
          onChange={(e) => setCari(e.target.value)}
          placeholder="Cari email pengguna"
          aria-label="Cari pengguna untuk diberi akses khusus"
        />

        {cari.trim().length >= 2 && (
          <div className="mt-1.5 overflow-hidden rounded-lg border border-[var(--border-subtle)]">
            {kandidat.length === 0 ? (
              <p className="px-3 py-2.5 text-xs text-[var(--text-muted)]">
                Tidak ada pengguna yang cocok.
              </p>
            ) : (
              kandidat.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => {
                    onUbahPengguna([...new Set([...(penggunaKhusus ?? []), u.id])]);
                    setCari('');
                  }}
                  className="flex w-full items-center justify-between gap-3 border-b border-[var(--border-subtle)] px-3 py-2 text-left last:border-0 hover:bg-[var(--surface-sunken)]"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-xs font-semibold text-[var(--text-primary)]">
                      {u.name}
                    </span>
                    <span className="block truncate text-[11px] text-[var(--text-muted)]">
                      {u.email}
                    </span>
                  </span>
                  <Plus className="size-3.5 shrink-0 text-accent-600 dark:text-accent-400" />
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Panel dev: mendaftarkan skrip Python sebagai template yang bisa dijalankan
 * user dari halaman scraping, tanpa user menyentuh kode sama sekali.
 */
export const DevTemplatesPage = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [formTerbuka, setFormTerbuka] = useState(null); // null | 'baru' | objek template
  const [hapusTarget, setHapusTarget] = useState(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['dev-templates'],
    queryFn: () => api.get('/platform/templates'),
  });

  const hapus = useMutation({
    mutationFn: () => api.delete(`/platform/templates/${hapusTarget.id}`),
    onSuccess: () => {
      toast.success('Template dihapus');
      setHapusTarget(null);
      queryClient.invalidateQueries({ queryKey: ['dev-templates'] });
    },
    onError: (err) =>
      toast.error('Gagal menghapus', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  const daftar = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">Template Skrip</h1>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Bungkus skrip Python di folder lab/ menjadi formulir yang bisa dijalankan user.
            Taruh berkasnya di server dulu, lalu daftarkan di sini.
          </p>
        </div>
        <Button icon={Plus} onClick={() => setFormTerbuka('baru')}>
          Template Baru
        </Button>
      </div>

      <Card>
        {isLoading ? (
          <TableSkeleton rows={4} columns={6} />
        ) : isError ? (
          <ErrorState message={error?.message} requestId={error?.requestId} onRetry={refetch} />
        ) : daftar.length === 0 ? (
          <EmptyState
            icon={Wrench}
            title="Belum ada template"
            description="Daftarkan skrip pertama supaya user bisa scraping tanpa mengisi selektor."
            action={
              <Button icon={Plus} onClick={() => setFormTerbuka('baru')}>
                Template Baru
              </Button>
            }
          />
        ) : (
          <TableWrapper>
            <Table>
              <thead>
                <Tr>
                  <Th>Nama</Th>
                  <Th>Website / Bagian</Th>
                  <Th>Kategori</Th>
                  <Th>Skrip</Th>
                  <Th>Status</Th>
                  <Th>Tindakan</Th>
                </Tr>
              </thead>
              <tbody>
                {daftar.map((t) => (
                  <Tr key={t.id}>
                    <Td>
                      <p className="font-semibold text-[var(--text-primary)]">{t.name}</p>
                      <p className="text-xs text-[var(--text-muted)]">
                        {t.params.length} parameter
                      </p>
                    </Td>
                    <Td className="text-[var(--text-secondary)]">
                      {t.website} / {t.section}
                    </Td>
                    <Td>
                      <Badge tone="neutral" size="sm">
                        {KATEGORI[t.category] ?? t.category}
                      </Badge>
                    </Td>
                    <Td>
                      <code className="font-mono text-xs text-[var(--text-muted)]">
                        {t.scriptPath}
                      </code>
                    </Td>
                    <Td>
                      <Badge tone={t.active ? 'success' : 'neutral'} size="sm">
                        {t.active ? 'Aktif' : 'Nonaktif'}
                      </Badge>
                    </Td>
                    <Td>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={Pencil}
                          iconOnly
                          aria-label={`Sunting ${t.name}`}
                          onClick={() => setFormTerbuka(t)}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={Trash2}
                          iconOnly
                          aria-label={`Hapus ${t.name}`}
                          className="text-danger-500 hover:text-danger-600"
                          onClick={() => setHapusTarget(t)}
                        />
                      </div>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>

      {formTerbuka !== null && (
        <FormTemplate
          awal={formTerbuka === 'baru' ? null : formTerbuka}
          onClose={() => setFormTerbuka(null)}
          onSukses={() => {
            setFormTerbuka(null);
            queryClient.invalidateQueries({ queryKey: ['dev-templates'] });
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(hapusTarget)}
        onClose={() => setHapusTarget(null)}
        onConfirm={() => hapus.mutate()}
        loading={hapus.isPending}
        destructive
        title={`Hapus template "${hapusTarget?.name}"?`}
        description="Template hilang dari halaman user. Berkas skripnya di server tidak disentuh."
        confirmLabel="Ya, hapus"
      />
    </div>
  );
};
